import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import type { TelesalesScope } from '../api/types';
import { Modal } from '../components/Modal';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

// ScopeDialog: Für welche Makler bzw. Gebiete ein Telefonist telefoniert (Leitung). Ohne Auswahl: alle Leads.
export function ScopeDialog({ userId, name, current, fieldSales, onClose }: {
  userId: string;
  name: string;
  current: TelesalesScope;
  fieldSales: { user_id: string; name: string }[];
  onClose: () => void;
}) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const areas = useQuery({ queryKey: [...projectKeys.areas(project.id), 'active'], queryFn: ({ signal }) => papi.projectAreas(false, signal), staleTime: 5 * 60_000 });
  const [makler, setMakler] = useState(() => new Set(current.field_sales.map((m) => m.id)));
  const [areaIds, setAreaIds] = useState(() => new Set(current.areas.map((a) => a.id)));
  const toggle = (set: Set<string>, update: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    update(next);
  };
  const save = useMutation({
    mutationFn: () => papi.setTelesalesScope(userId, { field_sales_user_ids: [...makler], area_ids: [...areaIds] }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.team(project.id) });
      void queryClient.invalidateQueries({ queryKey: ['projects', project.id, 'queue-peek'] });
      void queryClient.invalidateQueries({ queryKey: projectKeys.queueStats(project.id) });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Modal title={`Zuständigkeit von ${name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <p className="muted">
          {name} bekommt in der Akquise nur Leads der gewählten Makler bzw. Gebiete. Ohne Auswahl telefoniert {name} alle Leads des Projekts.
        </p>
        <fieldset className="scope-group">
          <legend>Makler</legend>
          {fieldSales.length === 0 && <p className="muted">Keine Makler im Projekt.</p>}
          {fieldSales.map((m) => (
            <label key={m.user_id} className="checkbox">
              <input type="checkbox" checked={makler.has(m.user_id)} onChange={() => toggle(makler, setMakler, m.user_id)} /> {m.name}
            </label>
          ))}
        </fieldset>
        <fieldset className="scope-group">
          <legend>Gebiete</legend>
          {areas.isPending && <p className="muted">Gebiete werden geladen …</p>}
          {areas.data?.items.length === 0 && <p className="muted">Keine Gebiete im Projekt.</p>}
          {areas.data?.items.map((a) => (
            <label key={a.id} className="checkbox">
              <input type="checkbox" checked={areaIds.has(a.id)} onChange={() => toggle(areaIds, setAreaIds, a.id)} /> {a.name}
            </label>
          ))}
        </fieldset>
        {save.isError && <MutationError error={save.error} />}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>
            Speichern
          </button>
        </div>
      </form>
    </Modal>
  );
}
